import { useModal } from "@/app/hooks/ui_hooks/useModal";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useLoader } from "@/app/hooks/useLoader";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Fragment, useEffect, useRef } from "react";
import BackdropComponent from "../../Backdrop";
import { IModalDoubleActionProps } from "../modals_interface";

const ModalDoubleAction: React.FC<IModalDoubleActionProps> = ({
  modalId,
  title,
  titleIcon,
  showIcon,
  contentOverflow,
  content,
  onVisibleCallback,
  titleIconContainerClassName,
  isConfirmLayout,
  button,
  ...props
}) => {
  const isAppRequestInProgress = useLoader("isSendAppRequestInProgress");
  const { setInvisible } = useModalActions(modalId);
  const isShowing = useModal(modalId);
  const isButtonOptionsProvided = button ? true : false;
  const cancelButtonRef = useRef<HTMLButtonElement>(null); // Ensure ref type

  const handlePrimaryAction = () => {
    if (isButtonOptionsProvided && button?.primaryCallback) {
      if (button?.primaryCallback) {
        button?.primaryCallback();
        if (button?.shouldHideOnClick && button?.shouldHideOnClick === true) {
          !isAppRequestInProgress && setInvisible();
        }
      }
    } else {
      setInvisible();
    }
  };

  const handleSecondaryAction = () => {
    if (isButtonOptionsProvided && button?.secondaryCallback) {
      if (button?.secondaryCallback) {
        button?.secondaryCallback();
        if (button?.shouldHideOnClick && button?.shouldHideOnClick === true) {
          !isAppRequestInProgress && setInvisible();
        }
      }
    } else {
      setInvisible();
    }
  };

  useEffect(() => {
    if (isShowing) {
      if (onVisibleCallback) {
        onVisibleCallback();
      }
    }
  }, [isShowing]);

  return (
    <Transition.Root show={isShowing} as={Fragment}>
      <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={setInvisible}>
        <BackdropComponent blur={props?.backdropBlur} />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  "relative transform  rounded-lg bg-white px-4 pb-4 pt-5 text-left border border-gray-200 transition-all sm:my-8 sm:w-full sm:p-6",
                  isConfirmLayout ? "sm:max-w-md" : "sm:max-w-3xl",
                  contentOverflow ? "overflow-visible" : "overflow-hidden",
                )}
              >
                <div className={clsx(isConfirmLayout && "flex items-start gap-3")}>
                  {showIcon && (
                    <div
                      className={clsx(
                        isConfirmLayout
                          ? "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-50 ring-1 ring-inset ring-green-100"
                          : "mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100",
                        titleIconContainerClassName,
                      )}
                    >
                      {titleIcon && titleIcon}
                    </div>
                  )}
                  <div className={clsx(isConfirmLayout && "min-w-0 flex-1")}>
                    <div className="flex justify-between items-center">
                      <Dialog.Title
                        as="h3"
                        className={clsx("text-base font-medium leading-6 text-gray-900", isConfirmLayout && "truncate")}
                      >
                        {title ?? "Add Title"}
                      </Dialog.Title>
                      <div onClick={() => setInvisible()} className="cursor-pointer">
                        <XMarkIcon className="h-4 w-4" />
                      </div>
                    </div>
                    {!isConfirmLayout && <hr className="my-3" />}
                    <div className={clsx(isConfirmLayout ? "mt-1" : "mt-2")}>
                      <p className="text-sm text-gray-500">{content ?? "Description to be added"}</p>
                    </div>
                  </div>
                </div>

                <div className={clsx(isConfirmLayout ? "flex justify-end pt-6 gap-3" : " flex justify-end p-4 gap-4")}>
                  {button?.secondaryButtonText !== "null" && (
                    <button
                      type="button"
                      className={clsx(
                        isConfirmLayout
                          ? "inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                          : "mt-3 inline-flex w-1/5 justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0",
                        button?.secondaryButtonClassname,
                      )}
                      onClick={isAppRequestInProgress ? null : handleSecondaryAction}
                      ref={cancelButtonRef}
                    >
                      {isButtonOptionsProvided ? button?.secondaryButtonText : "Secondary"}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={isAppRequestInProgress}
                    className={clsx(
                      isConfirmLayout
                        ? "inline-flex justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60"
                        : "inline-flex  justify-center rounded-md  px-6 py-2 text-sm font-semibold shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2  sm:col-start-2",
                      button?.primaryButtonClassname
                        ? button?.primaryButtonClassname
                        : "bg-indigo-600 text-white hover:bg-indigo-500focus-visible:outline-indigo-60 w-1/5",
                    )}
                    onClick={isAppRequestInProgress ? null : handlePrimaryAction}
                  >
                    {isAppRequestInProgress
                      ? "Sending..."
                      : isButtonOptionsProvided
                        ? button?.primaryButtonText
                        : "Primary"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ModalDoubleAction;
